import { Injectable, inject } from '@angular/core';

import { ParticipantMapping, StatisticsMode } from '@cds/query-objects';
import { DetailedFixtureFactory, OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferService, FixtureViewModel } from '@frontend/sports/betting-offer/feature/offer-service';
import { MarketGroupingConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { Observable, catchError, from, map, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OptionSheetViewDataService {
    private readonly bettingOfferService = inject(BettingOfferService);
    private readonly detailedFixtureFactory = inject(DetailedFixtureFactory);
    private readonly loggerFactory = inject(LoggerFactory);
    private readonly marketConfig = inject(MarketGroupingConfig);

    private readonly logger: SportsRemoteLogger;

    constructor() {
        this.logger = this.loggerFactory.getLogger('SheetViewCarouselDataService');
    }

    getFixture(
        event: EventModel,
        marketGroupItemIds: string,
        groupType: OptionGroupType,
        dynamicOfferCategory?: string,
    ): Observable<EventModel | undefined> {
        return from(
            this.bettingOfferService.getFixtureViewModel({
                id: event.id,
                statsMode: StatisticsMode.None,
                participantMapping: this.getParticipantMappingFilterValue(groupType),
                ...(dynamicOfferCategory ? { dynamicOfferCategories: dynamicOfferCategory } : { marketGroupItemIds }),
            }),
        ).pipe(
            map((fixtureDetails) => fixtureDetails && this.mapDetailedResponse(fixtureDetails)),
            catchError((error) => {
                this.logger.error(error, `Error fetching fixture for event ${event.id}`);
                return of(undefined);
            }),
        );
    }

    getEmptyOptionGroup(optionGroup: EventOptionGroup): EventOptionGroup {
        const emptyOptionGroup = new EventOptionGroup();
        emptyOptionGroup.detailedGrouping = optionGroup.detailedGrouping;
        emptyOptionGroup.options = [];
        emptyOptionGroup.subGroups = [];
        emptyOptionGroup.name = optionGroup.name;
        emptyOptionGroup.online = optionGroup.online;
        emptyOptionGroup.templateId = optionGroup.templateId;
        emptyOptionGroup.templateCategoryId = optionGroup.templateCategoryId;

        return emptyOptionGroup;
    }

    private mapDetailedResponse(fixtureDetails: FixtureViewModel): EventModel | undefined {
        return this.detailedFixtureFactory.create({
            fixture: fixtureDetails.fixture,
            splitFixtures: fixtureDetails.splitFixtures,
            grouping: fixtureDetails.grouping,
        });
    }

    private getParticipantMappingFilterValue(groupType: OptionGroupType): ParticipantMapping {
        return this.marketConfig.optionGroupWithPlayerData.includes(groupType) ? ParticipantMapping.All : ParticipantMapping.ExcludePlayer;
    }
}
